// Admin panel access (T-1102a/e, D46). Sign-in is a 6-digit code our bot sends to the staff
// member's Telegram; the API answers with an HttpOnly cookie, so no token is readable by page scripts.
import { z } from 'zod';

import { localizedNameSchema } from './localized.js';
import { ratingSummarySchema } from './reviews.js';

/** Every `/v1/admin/*` call except the sign-in must send this header (CSRF guard). */
export const ADMIN_CSRF_HEADER = 'x-spot-admin';
export const ADMIN_ROLES = ['moderator', 'admin'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Sign-in by a code from the bot (T-1102e): step 1, the staff member's Telegram @username. */
export const adminLoginCodeRequestSchema = z.strictObject({
	username: z
		.string()
		.trim()
		.transform((value) => value.replace(/^@/, ''))
		.pipe(z.string().regex(/^[A-Za-z0-9_]{5,32}$/)),
});
/** Always the same answer, staff or not, so usernames can't be probed. */
export const adminLoginCodeResponseSchema = z.object({ ok: z.literal(true) });
/** Step 2: the 6-digit code the bot sent, in the browser that asked for it. */
export const adminLoginVerifyRequestSchema = z.strictObject({
	code: z
		.string()
		.transform((value) => value.replace(/\s/g, ''))
		.pipe(z.string().regex(/^\d{6}$/)),
});
export const ADMIN_LOGIN_CODE_TTL_MINUTES = 5;
export const ADMIN_LOGIN_MAX_ATTEMPTS = 5;

export const adminMeSchema = z.object({
	id: z.uuid(),
	displayName: z.string().nullable(),
	role: z.enum(ADMIN_ROLES),
});

export const adminSignOutResponseSchema = z.object({ ok: z.literal(true) });

export type AdminMe = z.infer<typeof adminMeSchema>;

// ---- Admin API (T-1102b): reports queue, actions, users, error log ----

/** The moderators' chat actions (T-802), same names everywhere. */
export const MODERATION_ACTION_NAMES = [
	'hide_event',
	'unhide_event',
	'ban_user',
	'unban_user',
	'hide_message',
	'unhide_message',
	'dismiss_report',
	'unpause_user',
] as const;
export type ModerationActionName = (typeof MODERATION_ACTION_NAMES)[number];
export const ADMIN_USER_ACTIONS = ['unban_user', 'unpause_user'] as const;
/** These touch someone who may have done nothing: staff must say why (T-1105a). */
export const ADMIN_USER_REASONED_ACTIONS = ['ban_user', 'pause_user', 'hide_user_events'] as const;
export const ADMIN_MESSAGE_PREVIEW_MAX = 200;

const cursorQuery = {
	cursor: z.string().max(200).optional(),
	limit: z.coerce.number().int().min(1).max(50).default(20),
};
const person = z.object({ id: z.uuid(), displayName: z.string().nullable() });

export const adminReportsQuerySchema = z.object({
	status: z.enum(['open', 'resolved']).default('open'),
	/** Only reports on this user, their events and their messages (T-1105b). */
	userId: z.uuid().optional(),
	...cursorQuery,
});

/** What was reported, as moderators need to judge it; never exact places or contact data. */
export const adminReportTargetSchema = z.discriminatedUnion('type', [
	z.object({
		type: z.literal('event'),
		id: z.uuid(),
		title: z.string(),
		status: z.enum(['active', 'cancelled', 'completed']),
		hidden: z.boolean(),
		organizer: person,
	}),
	z.object({
		type: z.literal('user'),
		id: z.uuid(),
		displayName: z.string().nullable(),
		role: z.enum(['user', 'moderator', 'admin']),
		status: z.enum(['active', 'banned', 'deleted']),
		writesPaused: z.boolean(),
	}),
	z.object({
		type: z.literal('message'),
		id: z.uuid(),
		/** Cut to ADMIN_MESSAGE_PREVIEW_MAX characters. */
		preview: z.string(),
		hidden: z.boolean(),
		/** Null for system messages. */
		sender: person.nullable(),
		event: z.object({ id: z.uuid(), title: z.string() }),
	}),
]);

export const adminReportGroupSchema = z.object({
	/** The newest report on this target; actions are sent for it. */
	reportId: z.uuid(),
	targetType: z.enum(['event', 'user', 'message']),
	targetId: z.uuid(),
	/** Null when the target no longer exists. */
	target: adminReportTargetSchema.nullable(),
	reasons: z.array(z.object({ reason: z.string(), count: z.number().int() })),
	/** Reporters' notes, newest first (reporters themselves are not shown). */
	notes: z.array(z.string()),
	openCount: z.number().int(),
	resolvedCount: z.number().int(),
	firstReportedAt: z.iso.datetime(),
	lastReportedAt: z.iso.datetime(),
});

export const adminReportsResponseSchema = z.object({
	items: z.array(adminReportGroupSchema),
	nextCursor: z.string().nullable(),
});

export const adminReportParamsSchema = z.object({ reportId: z.uuid() });
export const adminReportActionRequestSchema = z.strictObject({
	action: z.enum(MODERATION_ACTION_NAMES),
});
export const adminReportActionResponseSchema = z.object({
	/** False when the action does not fit this target (e.g. banning a moderator). */
	applied: z.boolean(),
	undo: z.enum(MODERATION_ACTION_NAMES).nullable(),
});

const USER_ROLES = ['user', 'moderator', 'admin'] as const;
const USER_STATUSES = ['active', 'banned', 'deleted'] as const;

/** From this many risk points a user counts as «suspicious» (T-1105a). Never an automatic ban. */
export const ADMIN_RISK_SUSPICIOUS_SCORE = 5;
export const ADMIN_RISK_REASONS = [
	'reported_by_accounts',
	'blocked_by_accounts',
	'new_account_burst',
	'high_cancel_rate',
	'high_no_show_rate',
	'many_rejections',
] as const;
export const ADMIN_NOTE_MAX = 1000;
export const ADMIN_REASON_MAX = 500;

export const adminUsersQuerySchema = z.object({
	/** The Users tab's shortcut lists (T-1102c). */
	filter: z.enum(['banned', 'paused']).optional(),
	/** @username, a name, or a user id (T-1105a). */
	q: z.string().trim().min(1).max(100).optional(),
	status: z.enum(USER_STATUSES).optional(),
	role: z.enum(USER_ROLES).optional(),
	suspicious: z.literal('true').optional(),
	joinedAfter: z.iso.datetime().optional(),
	sort: z.enum(['newest', 'risk']).default('newest'),
	...cursorQuery,
});
export const adminUserRowSchema = z.object({
	id: z.uuid(),
	displayName: z.string().nullable(),
	telegramUsername: z.string().nullable(),
	role: z.enum(USER_ROLES),
	status: z.enum(USER_STATUSES),
	bannedReason: z.string().nullable(),
	writesPausedAt: z.iso.datetime().nullable(),
	reportCount: z.number().int(),
	riskScore: z.number().int(),
	createdAt: z.iso.datetime(),
});
export const adminUsersResponseSchema = z.object({
	items: z.array(adminUserRowSchema),
	nextCursor: z.string().nullable(),
});
export const adminUserParamsSchema = z.object({ userId: z.uuid() });

/** `GET /v1/admin/users/:userId` (T-1105a, D51): never an exact place, birth date or chat text. */
export const adminUserCardSchema = z.object({
	id: z.uuid(),
	displayName: z.string().nullable(),
	telegramUsername: z.string().nullable(),
	/** `admin` role only; null for moderators. A string: it does not fit a JS number safely. */
	telegramId: z.string().nullable(),
	photoUrl: z.url().nullable(),
	about: z.string().nullable(),
	interests: z.array(z.object({ slug: z.string(), name: localizedNameSchema })),
	age: z.number().int().nullable(),
	role: z.enum(USER_ROLES),
	status: z.enum(USER_STATUSES),
	bannedReason: z.string().nullable(),
	writesPausedAt: z.iso.datetime().nullable(),
	verifiedAt: z.iso.datetime().nullable(),
	joinedAt: z.iso.datetime(),
	lastSeenAt: z.iso.datetime().nullable(),
	counters: z.object({
		eventsCreated: z.number().int(),
		eventsCancelled: z.number().int(),
		applicationsSent: z.number().int(),
		applicationsAccepted: z.number().int(),
		applicationsRejected: z.number().int(),
		noShows: z.number().int(),
		blocksByOthers: z.number().int(),
		reportsFiled: z.number().int(),
	}),
	rating: ratingSummarySchema,
	/** On the user, their events and their messages. Reasons only: reporters stay unknown. */
	reportsAgainst: z.object({
		total: z.number().int(),
		open: z.number().int(),
		reasons: z.array(z.object({ reason: z.string(), count: z.number().int() })),
	}),
	risk: z.object({
		score: z.number().int(),
		suspicious: z.boolean(),
		reasons: z.array(
			z.object({
				code: z.enum(ADMIN_RISK_REASONS),
				points: z.number().int(),
				count: z.number().int(),
			}),
		),
	}),
	recentEvents: z.array(
		z.object({
			id: z.uuid(),
			title: z.string(),
			status: z.enum(['active', 'cancelled', 'completed']),
			hidden: z.boolean(),
			startAt: z.iso.datetime(),
			/** Nearest metro, as on a public card; never the exact place. */
			area: localizedNameSchema.nullable(),
		}),
	),
	history: z.array(
		z.object({
			id: z.uuid(),
			action: z.string(),
			/** Null for automatic actions. */
			moderatorName: z.string().nullable(),
			note: z.string().nullable(),
			at: z.iso.datetime(),
		}),
	),
	notes: z.array(
		z.object({
			id: z.uuid(),
			body: z.string(),
			authorName: z.string().nullable(),
			at: z.iso.datetime(),
		}),
	),
});

const reasonText = z.string().trim().min(3).max(ADMIN_REASON_MAX);
export const adminUserActionRequestSchema = z.discriminatedUnion('action', [
	z.strictObject({ action: z.enum(ADMIN_USER_ACTIONS), reason: reasonText.optional() }),
	z.strictObject({ action: z.enum(ADMIN_USER_REASONED_ACTIONS), reason: reasonText }),
]);
export const adminUserNoteRequestSchema = z.strictObject({
	body: z.string().trim().min(1).max(ADMIN_NOTE_MAX),
});
export const adminUserNoteResponseSchema = z.object({ id: z.uuid() });
export const adminUserFlagsRequestSchema = z.strictObject({
	verified: z.boolean(),
	reason: reasonText,
});
export const adminUserRoleRequestSchema = z.strictObject({
	role: z.enum(USER_ROLES),
	reason: reasonText,
});
export const adminUserActionResponseSchema = z.object({ applied: z.boolean() });

/** Error log (T-1101), admins only: the latest groups; rows are kept 30 days. */
export const adminErrorsQuerySchema = z.object({
	source: z.enum(['api', 'job', 'web']).optional(),
	limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const adminErrorGroupSchema = z.object({
	fingerprint: z.string(),
	source: z.enum(['api', 'job', 'web']),
	count: z.number().int(),
	firstAt: z.iso.datetime(),
	lastAt: z.iso.datetime(),
	sample: z.object({
		name: z.string(),
		message: z.string(),
		route: z.string().nullable(),
		frames: z.string().nullable(),
		requestId: z.string().nullable(),
	}),
});
export const adminErrorsResponseSchema = z.object({ items: z.array(adminErrorGroupSchema) });

export type AdminReportGroup = z.infer<typeof adminReportGroupSchema>;
export type AdminReportTarget = z.infer<typeof adminReportTargetSchema>;
export type AdminReportsResponse = z.infer<typeof adminReportsResponseSchema>;
export type AdminUserRow = z.infer<typeof adminUserRowSchema>;
export type AdminUserCard = z.infer<typeof adminUserCardSchema>;
export type AdminUserActionRequest = z.infer<typeof adminUserActionRequestSchema>;
export type AdminUsersResponse = z.infer<typeof adminUsersResponseSchema>;
export type AdminErrorGroup = z.infer<typeof adminErrorGroupSchema>;
export type AdminErrorsResponse = z.infer<typeof adminErrorsResponseSchema>;
